import { Icon, Text } from '@pairy/pds-react';
import { useIsomorphicLayoutEffect } from '@pairy/react-hooks';
import useResizeObserver from '@react-hook/resize-observer';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useMediaQuery } from 'react-responsive';

import {
  isCtaModalOpenAtom,
  isDisableHelpersAtom,
  pdfViewerModeAtom,
} from '../../../store/index.atom';
import {
  v2ContainerSizeAtom,
  v2IsImmersiveAtom,
  v2IsThumbnailPanelOpenAtom,
  v2ViewModeAtom,
  v2ZoomAtom,
  v2ZoomFitModeAtom,
} from '../../../store/v2/viewer.atom';
import useFitZoom from '../../../hooks/v2/useFitZoom';
import useFitWidthHint from '../../../hooks/v2/useFitWidthHint';
import useFullscreen from '../../../hooks/v2/useFullscreen';
import useV2KeyPress from '../../../hooks/v2/useV2KeyPress';
import useV2PageNavigation from '../../../hooks/v2/useV2PageNavigation';
import { getBlindPages } from '../../BulkViewer/index.util';
import { isPreviewFormSubmittedAtom } from '../../BulkPdfPreview/index.atom';
import DesktopCtaFormLayer from '../../BulkViewer/Desktop/CtaFormLayer';
import DesktopCtaButtonLayer from '../../BulkViewer/Desktop/CtaButtonLayer';
import MobileCtaFormLayer from '../../BulkViewer/Mobile/CtaFormLayer';
import MobileCtaButtonLayer from '../../BulkViewer/Mobile/CtaButtonLayer';
import DesktopIntro from '../../@common/Intro/Desktop';
import MobileIntro from '../../@common/Intro/Mobile';
import { EmbedItemType, MessageBoxItemType } from '../../PdfViewer/index.types';
import { V2CtaConfigType, V2DocumentType } from '../index.types';
import { clampV2Zoom } from '../zoom.constants';
import Toolbar from '../Toolbar';
import MobileBottomBar from '../MobileBottomBar';
import ThumbnailPanel from '../ThumbnailPanel';
import ProgressBar from '../ProgressBar';
import FitWidthHint from '../FitWidthHint';
import PagedViewer from '../PagedViewer';
import ScrollViewer from '../ScrollViewer';

import * as Styled from './index.styles';

export interface V2ViewerShellProps {
  document: V2DocumentType;
  isBulk: boolean;
  domain?: string | null;
  messageBoxItems?: MessageBoxItemType[];
  embedItems?: EmbedItemType[];
  cta?: V2CtaConfigType;
  translateKoToEn: (item: string) => string;
  /** 서버 설정 연동을 대비한 스크롤 모드 가용성 플래그(현재 기본 true) */
  isScrollModeEnabled?: boolean;
  /** 관리자 미리보기 모드(추적 미수집·CTA 프리뷰 제출 상태 사용·버전 전환 숨김) */
  isPreview?: boolean;
}

/** V2 뷰어 레이아웃 루트. 툴바/패널/뷰어/공유 오버레이를 합성한다. */
const V2ViewerShell = ({
  document: pdfDocument,
  isBulk,
  domain,
  messageBoxItems,
  embedItems,
  cta,
  translateKoToEn,
  isScrollModeEnabled = true,
  isPreview = false,
}: V2ViewerShellProps) => {
  const userAgent =
    typeof window !== 'undefined' ? window.navigator.userAgent : '';
  const isMobile =
    useMediaQuery({ maxWidth: 767 }) || /iPhone|Android/.test(userAgent);
  const isMobileLandscape =
    useMediaQuery({ query: '(orientation: landscape)' }) && isMobile;

  const shellRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const setPdfViewerMode = useSetAtom(pdfViewerModeAtom);
  const setContainerSize = useSetAtom(v2ContainerSizeAtom);
  const [viewMode, setViewMode] = useAtom(v2ViewModeAtom);
  const setZoom = useSetAtom(v2ZoomAtom);
  const fitMode = useAtomValue(v2ZoomFitModeAtom);
  const [isThumbnailPanelOpen, setIsThumbnailPanelOpen] = useAtom(
    v2IsThumbnailPanelOpenAtom,
  );
  const [isImmersive, setIsImmersive] = useAtom(v2IsImmersiveAtom);
  const [isDisableHelpers, setIsDisableHelpers] = useAtom(isDisableHelpersAtom);
  const isCtaModalOpen = useAtomValue(isCtaModalOpenAtom);
  const isPreviewFormSubmitted = useAtomValue(isPreviewFormSubmittedAtom);

  const { isFullscreenActive, toggleFullscreen } = useFullscreen(shellRef);

  /*
   * 데스크톱은 페이지 목록을 기본 오픈한다(최초 1회, 이후 사용자 토글 우선).
   * useMediaQuery는 하이드레이션 직후 false를 반환할 수 있어
   * 이펙트 시점에 matchMedia로 직접 판별한다(모바일 기본 닫힘 보장).
   */
  const isPanelDefaultAppliedRef = useRef(false);
  useIsomorphicLayoutEffect(() => {
    if (isPanelDefaultAppliedRef.current) {
      return;
    }
    isPanelDefaultAppliedRef.current = true;

    const isMobileEnvironment =
      window.matchMedia('(max-width: 767px)').matches ||
      /iPhone|Android/.test(window.navigator.userAgent);

    if (!isMobileEnvironment) {
      setIsThumbnailPanelOpen(true);
    }
  }, [setIsThumbnailPanelOpen]);

  /* V1 RootLayout과 동일한 모드 등록 + 바디 스크롤 잠금 + 모바일 높이 보정 */
  useIsomorphicLayoutEffect(() => {
    setPdfViewerMode(isMobile ? 'mobile' : 'desktop');

    const { body } = document;
    disableBodyScroll(body, { allowTouchMove: () => true });

    return () => {
      enableBodyScroll(body);
    };
  }, [isMobile, setPdfViewerMode]);

  useIsomorphicLayoutEffect(() => {
    const documentHeight = () => {
      /*
       * iOS Safari/인앱 브라우저는 주소창 표시·숨김 시 window resize가
       * 늦거나 오지 않는다. visualViewport가 실제 보이는 영역을 추적한다.
       */
      const height = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--doc-height', `${height}px`);
    };

    documentHeight();
    window.addEventListener('resize', documentHeight);
    window.visualViewport?.addEventListener('resize', documentHeight);

    return () => {
      window.removeEventListener('resize', documentHeight);
      window.visualViewport?.removeEventListener('resize', documentHeight);
    };
  }, []);

  /* 콘텐츠 영역 측정 → fit 줌 계산의 기준 */
  useResizeObserver(contentRef, ({ contentRect: { width, height } }) =>
    setContainerSize({ width, height }),
  );

  /* 스크롤 모드 비활성 시 paged 강제 */
  useEffect(() => {
    if (!isScrollModeEnabled && viewMode === 'scroll') {
      setViewMode('paged');
    }
  }, [isScrollModeEnabled, viewMode, setViewMode]);

  const ctaForm = cta?.ctaForm;
  const ctaFormPage = ctaForm?.page;
  const blindMode = ctaForm?.blind;
  /* 미리보기에서는 프리뷰 폼 제출 상태(atom)를 실제 제출로 간주한다(V1 파리티) */
  const isFormSubmitted =
    cta?.isFormSubmitted || (isPreview && isPreviewFormSubmitted);

  const isGateActive = Boolean(
    blindMode === 'startFrom' && !isFormSubmitted && ctaFormPage,
  );

  const blindPages = isFormSubmitted
    ? null
    : getBlindPages({
        blindStatus: blindMode,
        blindPage: ctaFormPage,
        totalPages: pdfDocument.totalPages,
      });

  const isRedirectCtaFormAppropriate = Boolean(
    cta && !(blindMode === 'none') && !isFormSubmitted,
  );

  const navigation = useV2PageNavigation({
    totalPages: pdfDocument.totalPages,
    ctaFormPage,
    isGateActive,
  });
  const { currentPage, goToPage, goToPrevPage, goToNextPage } = navigation;

  useV2KeyPress({ goToPrevPage, goToNextPage });

  const currentPageData =
    pdfDocument.pages[currentPage - 1] ?? pdfDocument.pages[0];
  const { fitWidthZoom } = useFitZoom({
    originalWidth: currentPageData?.originalWidth ?? 1,
    originalHeight: currentPageData?.originalHeight ?? 1,
  });

  /*
   * 세로로 긴 문서라도 배율은 자동으로 바꾸지 않는다.
   * 대신 가로 맞춤 힌트 칩으로 전환을 권유하고 선택은 사용자에게 맡긴다.
   */
  const { isHintVisible, applyFitWidth, dismissHint } = useFitWidthHint({
    fitWidthZoom,
  });

  /* fit-width 모드는 리사이즈/페이지 변경 시 배율을 추적한다(fit-page는 zoom=1 고정) */
  useEffect(() => {
    if (fitMode === 'fitWidth') {
      setZoom(clampV2Zoom(fitWidthZoom));
    }
    if (fitMode === 'fitPage') {
      setZoom(1);
    }
  }, [fitMode, fitWidthZoom, setZoom]);

  /* 벌크: 게이트 페이지 1페이지 설정 시 폼 제출 전 인트로 숨김(V1 파리티) */
  const isIntroVisible = cta
    ? ctaFormPage === 1 && blindMode === 'startFrom'
      ? Boolean(isFormSubmitted)
      : true
    : true;

  const isChromeVisible = !(isMobile && isImmersive);

  /*
   * 문의양식 딤/모달이 덮는 동안에는 힌트 칩을 렌더하지 않는다.
   * CtaFormLayer는 이펙트에서 isCtaModalOpenAtom을 켜므로 원자값만 보면
   * 모달이 붙기 전 한 커밋 동안 칩이 보였다가 딤에 가려진다.
   * 모달이 열릴 조건을 같은 기준(currentPageAtom)으로 동기 계산해 깜빡임을 막는다.
   */
  const isCtaOverlayVisible =
    isCtaModalOpen ||
    Boolean(
      ctaFormPage === currentPage && blindMode !== 'none' && !isFormSubmitted,
    );

  return (
    <Styled.ShellRoot ref={shellRef} as="main">
      {isIntroVisible &&
        (isMobile ? (
          <MobileIntro translateKoToEn={translateKoToEn} />
        ) : (
          <DesktopIntro translateKoToEn={translateKoToEn} />
        ))}

      {isMobile && isChromeVisible && (
        <>
          <ProgressBar
            currentPage={currentPage}
            totalPages={pdfDocument.totalPages}
          />
          {/* 가로 모드에서는 V1처럼 상단을 비워 세로 공간을 확보한다 */}
          {!isMobileLandscape && (
            <Styled.MobileTitleBar>
              <Text variant="sm-semibold" color="zinc_700">
                <Styled.MobileTitle>{pdfDocument.name}</Styled.MobileTitle>
              </Text>
            </Styled.MobileTitleBar>
          )}
        </>
      )}

      <Styled.LayoutRow>
        {/* 데스크톱 페이지 목록은 툴바 영역까지 포함한 풀 높이 좌측 패널 */}
        {!isMobile && isThumbnailPanelOpen && (
          <ThumbnailPanel
            document={pdfDocument}
            currentPage={currentPage}
            goToPage={goToPage}
            blindPages={blindPages}
            isGateActive={isGateActive}
            ctaFormPage={ctaFormPage}
            embedItems={embedItems}
            isMobile={false}
            translateKoToEn={translateKoToEn}
            onClose={() => setIsThumbnailPanelOpen(false)}
          />
        )}

        <Styled.MainColumn>
          {/* 목록이 닫힌 상태에서는 좌측 엣지 핸들로 다시 연다 */}
          {!isMobile && !isThumbnailPanelOpen && (
            <Styled.PanelEdgeHandle
              type="button"
              className={isDisableHelpers ? 'disabled' : ''}
              aria-label={translateKoToEn('thumbnails')}
              title={translateKoToEn('thumbnails')}
              onClick={(e) => {
                e.stopPropagation();
                /* 흐려진 상태의 첫 클릭은 컨트롤 표시만 복구한다(V1 파리티) */
                if (isDisableHelpers) {
                  setIsDisableHelpers(false);
                  return;
                }
                setIsThumbnailPanelOpen(true);
              }}
            >
              <Icon icon="ph:caret-double-right" size={16} color="black" />
            </Styled.PanelEdgeHandle>
          )}
          {!isMobile && (
            <Toolbar
              document={pdfDocument}
              isBulk={isBulk}
              domain={domain}
              isPreview={isPreview}
              ctaFormPage={ctaFormPage}
              isRedirectCtaFormAppropriate={isRedirectCtaFormAppropriate}
              isScrollModeEnabled={isScrollModeEnabled}
              currentPage={currentPage}
              goToPage={goToPage}
              goToPrevPage={goToPrevPage}
              goToNextPage={goToNextPage}
              isNextPageDisabled={navigation.isNextPageDisabled}
              fitWidthZoom={fitWidthZoom}
              isFullscreenActive={isFullscreenActive}
              toggleFullscreen={toggleFullscreen}
              translateKoToEn={translateKoToEn}
            />
          )}

          <Styled.Body isMobile={isMobile}>
            <Styled.ContentWrapper
              ref={contentRef}
              onClick={() => {
                if (isMobile && isImmersive) {
                  setIsImmersive(false);
                  return;
                }
                /* V1 파리티: 슬라이드 영역 클릭/탭으로 플로팅 컨트롤 표시 토글 */
                setIsDisableHelpers((prev) => !prev);
              }}
            >
              {viewMode === 'scroll' && isScrollModeEnabled ? (
                <ScrollViewer
                  document={pdfDocument}
                  currentPage={currentPage}
                  blindPages={blindMode === 'specific' ? blindPages : null}
                  isGateActive={isGateActive}
                  ctaFormPage={ctaFormPage}
                  messageBoxItems={messageBoxItems}
                  embedItems={embedItems}
                  ctaForm={ctaForm}
                  isFormSubmitted={isFormSubmitted}
                  isMobile={isMobile}
                  translateKoToEn={translateKoToEn}
                />
              ) : (
                <PagedViewer
                  document={pdfDocument}
                  currentPage={currentPage}
                  goToPage={goToPage}
                  goToPrevPage={goToPrevPage}
                  goToNextPage={goToNextPage}
                  isNextPageDisabled={navigation.isNextPageDisabled}
                  blindPages={blindPages}
                  messageBoxItems={messageBoxItems}
                  embedItems={embedItems}
                  ctaForm={ctaForm}
                  isFormSubmitted={isFormSubmitted}
                  isMobile={isMobile}
                />
              )}

              <FitWidthHint
                /*
                 * 모바일 몰입 모드·문의양식 오버레이 중에는 함께 숨긴다.
                 * 양식 제출로 오버레이가 걷히면 그때 자연스럽게 나타난다.
                 */
                isVisible={
                  isHintVisible && isChromeVisible && !isCtaOverlayVisible
                }
                isMobile={isMobile}
                onApply={applyFitWidth}
                onDismiss={dismissHint}
                translateKoToEn={translateKoToEn}
              />

              {/* 데스크톱 CTA 레이어는 슬라이드 영역(ContentWrapper) 기준으로
              딤/모달/버튼이 배치되도록 내부에 렌더한다 */}
              {isBulk && cta && !isMobile && (
                <>
                  <DesktopCtaFormLayer
                    ctaForm={ctaForm}
                    isPreview={isPreview}
                    isEmailVerificationEnabled={cta.isEmailVerificationEnabled}
                    isFormSubmitted={isFormSubmitted}
                    translateKoToEn={translateKoToEn}
                    userSubscriptionPlan={pdfDocument.plan}
                  />
                  <DesktopCtaButtonLayer ctaButtonList={cta.ctaButtonList} />
                </>
              )}
            </Styled.ContentWrapper>

            {isMobile && isThumbnailPanelOpen && (
              <ThumbnailPanel
                document={pdfDocument}
                currentPage={currentPage}
                goToPage={goToPage}
                blindPages={blindPages}
                isGateActive={isGateActive}
                ctaFormPage={ctaFormPage}
                embedItems={embedItems}
                isMobile
                translateKoToEn={translateKoToEn}
              />
            )}
          </Styled.Body>
        </Styled.MainColumn>
      </Styled.LayoutRow>

      {isMobile && isChromeVisible && (
        <MobileBottomBar
          document={pdfDocument}
          isBulk={isBulk}
          domain={domain}
          isPreview={isPreview}
          ctaFormPage={ctaFormPage}
          isRedirectCtaFormAppropriate={isRedirectCtaFormAppropriate}
          isScrollModeEnabled={isScrollModeEnabled}
          currentPage={currentPage}
          goToPrevPage={goToPrevPage}
          goToNextPage={goToNextPage}
          isNextPageDisabled={navigation.isNextPageDisabled}
          isFullscreenActive={isFullscreenActive}
          toggleFullscreen={toggleFullscreen}
          translateKoToEn={translateKoToEn}
          isMobileLandscape={isMobileLandscape}
        />
      )}

      {isBulk && cta && isMobile && (
        <>
          <MobileCtaFormLayer
            ctaForm={ctaForm}
            isPreview={isPreview}
            totalPage={pdfDocument.totalPages}
            isNextPageButtonDisabled={navigation.isNextPageDisabled}
            isEmailVerificationEnabled={cta.isEmailVerificationEnabled}
            isFormSubmitted={isFormSubmitted}
            translateKoToEn={translateKoToEn}
            userSubscriptionPlan={pdfDocument.plan}
            isMobileLandscape={isMobileLandscape}
          />
          <MobileCtaButtonLayer
            ctaButtonList={cta.ctaButtonList}
            isMobileLandscape={isMobileLandscape}
          />
        </>
      )}
    </Styled.ShellRoot>
  );
};

export default V2ViewerShell;
